import React, { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import Breadcrumb from '~/components/Breadcrumb'
import { Col, Hide, Show } from '~/components/commons'
import * as Icon from 'react-feather'
import {
  Card,
  CardTitle,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  Input,
  Button,
  Spinner,
} from 'reactstrap'
import Textarea from '~/components/Textarea'
import Select from '~/components/Select'
import classnames from 'classnames'
import useAxios from 'axios-hooks'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { Alert } from '~/components/Alert'

const NavItemLink = styled(NavItem)`
  margin: 8px 0 12px !important;
  position: relative;

  a {
    color: #626262;
  }

  .active {
    font-weight: 600;
    color: #7367f0;
    &::after {
      content: attr(data-before);
      height: 2px;
      width: 15px;
      position: absolute !important;
      right: -1.5rem;
      bottom: 100%;
      top: 1rem;
      width: 2.14rem;
      transition: all 0.2s linear;
      background: linear-gradient(
        30deg,
        #7367f0,
        rgba(115, 103, 240, 0.5)
      ) !important;
      transform: rotate(90deg);
      box-shadow: 0 0 8px 0 rgb(115 103 240 / 50%) !important;
    }
  }
`

const TemplateCard = ({ name, onEditing, currentLocation }) => {
  const IconTag = location => {
    switch (location) {
      case 'reviews':
        return <Icon.Star />
      case 'questions':
        return <Icon.HelpCircle />
      default:
        return <Icon.Star />
    }
  }
  return (
    <div
      style={{
        background: '#FDFDFD',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        borderRadius: '8px',
        width: '100%',
        height: '70px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 15px',
        marginBottom: '20px',
      }}
    >
      <div>
        {IconTag(currentLocation)}
        <span className="ml-1">{name}</span>
      </div>
      <Button.Edit onClick={onEditing}>
        <Icon.Edit2 size={14} style={{ color: '#929292' }} />
        <FormattedMessage id="Edit" />
      </Button.Edit>
    </div>
  )
}

const customStyles = {
  container: provided => ({
    ...provided,
    minWidth: '200px',
    borderRadius: '8px',
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: 'pointer',
    paddingLeft: '30px',
    color: state.isSelected ? '#FFF' : '#626262',
    backgroundColor: state.isSelected
      ? '#7367f0'
      : state.isFocused
      ? '#f2f2f2'
      : 'transparent',
  }),

  control: provided => ({
    ...provided,
    border: '1px solid #D7D7D7',
    boxShadow: 'none',
    cursor: 'pointer',
  }),

  placeholder: provided => ({
    ...provided,
    color: '#979797',
  }),

  singleValue: (provided, state) => ({
    ...provided,
    opacity: state.isDisabled ? 0.5 : 1,
    transition: 'opacity 300ms',
    zIndex: 2,
  }),

  menu: base => ({
    ...base,
    zIndex: 1000,
  }),

  menuList: base => ({
    ...base,
    boxShadow: 'none',
    transition: 'all 2.75s ease-in-out',
    transform: '',
    overflowY: 'auto !important',
    zIndex: 1000,
  }),
}

export function VariablesSelect(props) {
  return (
    <Select
      {...props}
      isSearchable={true}
      styles={customStyles}
      menuShouldScrollIntoView={true}
    />
  )
}

function CreateTemplateCard({
  currentLocation,
  templates = [],
  loading,
  onCreate,
  onUpdate,
  onDelete,
  submiting,
  isInvalid,
}) {
  const [name, setName] = useState('')
  const [content, setContent] = useState('')
  const [templateInfo, setTemplateInfo] = useState({})
  const [isEditing, setIsEditing] = useState(false)

  const intl = useIntl()

  const variableOptions = [
    {
      label: intl.formatMessage({ id: 'Local name' }),
      value: '{{local_name}}',
    },
    {
      label: intl.formatMessage({ id: 'Local city' }),
      value: '{{local_city}}',
    },
    {
      label: intl.formatMessage({ id: 'Local area' }),
      value: '{{local_area}}',
    },
    {
      label: intl.formatMessage({ id: 'Local state' }),
      value: '{{local_state}}',
    },
    {
      label: intl.formatMessage({ id: 'Store code' }),
      value: '{{store_code}}',
    },
    {
      label: intl.formatMessage({ id: 'First name' }),
      value: '{{first_name}}',
    },
  ]

  const clearForm = () => {
    setName('')
    setContent('')
  }

  return (
    <Card className="py-2 px-2 px-md-3 mt-0 mx-0 mx-md-1 mb-3">
      <CardTitle style={{ marginBottom: '20px' }}>
        <FormattedMessage
          id={`Create new type template`}
          values={{ type: intl.formatMessage({ id: currentLocation }) }}
        />
      </CardTitle>
      <label
        style={{ margin: '25px 0 5px' }}
        className={isInvalid && !name ? 'text-danger' : ''}
      >
        <FormattedMessage id="Template name" /> *
      </label>
      <Input
        className={isInvalid && !name ? 'border-danger' : ''}
        style={{ maxWidth: '500px' }}
        value={name}
        onChange={e => setName(e?.target?.value)}
      />
      <label
        style={{ margin: '25px 0 5px' }}
        className={isInvalid && !content ? 'text-danger' : ''}
      >
        <FormattedMessage id="Content" /> *
      </label>
      <Textarea
        className={isInvalid && !content ? 'border-danger' : ''}
        value={content}
        resize
        onChange={e => setContent(e?.target?.value)}
      />
      <div className="buttons d-flex mt-1 mb-3 flex-column-reverse flex-md-row">
        <Hide condition={isEditing}>
          <Button
            color="success"
            className="mr-md-2 mr-0 mt-2 mt-md-0"
            onClick={() => onCreate({ name, content, clearForm })}
            disabled={submiting}
          >
            {intl.formatMessage({ id: 'Create new template' }).toUpperCase()}
          </Button>
        </Hide>
        <Show condition={isEditing}>
          <Button
            color="primary"
            className="mr-2"
            onClick={() =>
              onUpdate({
                name,
                content,
                clearForm,
                setIsEditing,
                id: templateInfo.id,
              })
            }
            disabled={submiting}
          >
            {intl.formatMessage({ id: 'Save' }).toUpperCase()}
          </Button>
        </Show>
        <Show condition={isEditing}>
          <Button
            className="mr-2"
            onClick={() => {
              setTemplateInfo({})
              clearForm()
              setIsEditing(false)
            }}
            disabled={submiting}
          >
            {intl.formatMessage({ id: 'Cancel' }).toUpperCase()}
          </Button>
        </Show>
        <VariablesSelect
          options={variableOptions}
          style={{ minWidth: '200px !important' }}
          onChange={({ value }) => setContent(prev => prev + value)}
          placeholder={<FormattedMessage id="Variables" />}
        />
        <Show condition={isEditing}>
          <Button.Ripple
            color="danger"
            className="ml-2"
            onClick={() =>
              onDelete({ id: templateInfo.id, clearForm, setIsEditing })
            }
            disabled={submiting}
          >
            <Icon.Trash2 size={15} style={{ marginRight: '5px' }} />
            {intl.formatMessage({ id: 'Delete' }).toUpperCase()}
          </Button.Ripple>
        </Show>
      </div>

      <CardTitle>
        <FormattedMessage id="My Templates" />
      </CardTitle>
      <div className="card-list mt-1" style={{ minHeight: '300px' }}>
        <Show condition={templates?.length && !loading}>
          {templates?.map(t => (
            <TemplateCard
              key={t.id}
              name={t.name}
              isEditing={isEditing}
              currentLocation={currentLocation}
              onEditing={() => {
                setName(t.name)
                setContent(t.content)
                setTemplateInfo(t)
                setIsEditing(true)
              }}
            />
          ))}
        </Show>
        <Hide condition={templates?.length || loading}>
          <FormattedMessage id="No template have been created yet." />
        </Hide>
        <Show condition={loading}>
          <Spinner />
        </Show>
      </div>
    </Card>
  )
}

function TextTemplates() {
  const [alertVisible, setAlertVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [isInvalid, setIstInvalid] = useState(false)
  const location = useLocation()
  const currentLocation = location.pathname?.split('/')?.[2] || ''

  const accountId = useSelector(state => state?.user?.profile?.account?.id)

  function openModal(status) {
    setModalMessage(`Template successfully ${status}!`)
    setAlertVisible(true)
  }

  const [
    { data: templates, loading },
  ] = useAxios(`${location.pathname}/${accountId}`, { useCache: false })

  const [{ loading: creating }, createTemplate] = useAxios(
    { url: `${location.pathname}/${accountId}`, method: 'POST' },
    { useCache: false, manual: true }
  )

  const [{ loading: updating }, updateTemplate] = useAxios(
    { url: `${location.pathname}/${accountId}`, method: 'PATCH' },
    { useCache: false, manual: true }
  )
  const [{ _loading: deleting }, deleteTemplate] = useAxios(
    { url: `${location.pathname}/${accountId}`, method: 'DELETE' },
    { useCache: false, manual: true }
  )

  const handleCreate = async ({ name, content, clearForm }) => {
    if (!name || !content) return setIstInvalid(true)
    createTemplate({ data: { name, content } })
      .then(({ data }) => {
        setIstInvalid(false)
        templates.push(data)
        clearForm()
        openModal('created')
      })
      .catch(e => {
        const { message } = e?.response?.data || {}
        const errorMessage = 'Error on template creation, try again later.'
        toast.error(message ?? errorMessage)
      })
  }

  const handleUpdate = async ({
    name,
    content,
    clearForm,
    setIsEditing,
    id,
  }) => {
    if (!name || !content) return setIstInvalid(true)
    updateTemplate({ data: { name, content, id } })
      .then(({ data }) => {
        setIstInvalid(false)
        const index = templates?.findIndex(t => t.id === id)
        templates.splice(index, 1, data)
        clearForm()
        setIsEditing(false)
        openModal('updated')
      })
      .catch(e => {
        const { message } = e?.response?.data || {}
        const errorMessage = 'Error on template update, try again later.'
        toast.error(message ?? errorMessage)
      })
  }

  const handleDelete = async ({ clearForm, setIsEditing, id }) => {
    deleteTemplate({ data: { id } })
      .then(() => {
        const index = templates?.findIndex(t => t.id === id)
        templates.splice(index, 1)
        clearForm()
        setIsEditing(false)
        openModal('deleted')
      })
      .catch(e => {
        console.error(e)
        const { message } = e?.response?.data || {}
        const errorMessage = 'Error on template delete, try again later.'
        toast.error(message ?? errorMessage)
      })
  }

  return (
    <>
      <Col>
        <Breadcrumb
          className="mb-2"
          paths={[
            {
              render: () => (
                <Link className="d-flex align-items-center" to="/">
                  <Icon.Home size={20} />
                </Link>
              ),
            },
            { text: 'Templates' },
            { text: _.capitalize(currentLocation) },
          ]}
        />
        <div className="nav-vertical d-flex flex-column flex-md-row w-100">
          <Nav
            tabs
            className="nav-left mt-1 "
            style={{ minWidth: '150px', boxShadow: 'none' }}
          >
            <NavItemLink>
              <NavLink
                className={classnames({
                  active: location.pathname === '/templates/reviews',
                })}
                to={'/templates/reviews'}
              >
                <Icon.Star className="mr-1" />
                <FormattedMessage id="Reviews" />
              </NavLink>
            </NavItemLink>
            <NavItemLink>
              <NavLink
                className={classnames({
                  active: location.pathname === '/templates/questions',
                })}
                to={'/templates/questions'}
              >
                <Icon.HelpCircle className="mr-1" />
                <FormattedMessage id="Questions" />
              </NavLink>
            </NavItemLink>
          </Nav>
          <TabContent activeTab={location.pathname} className="mt-1 w-100">
            <TabPane
              tabId="/templates/questions"
              className="bg-transparent mx-0 pl-0 pl-md-2"
            >
              <CreateTemplateCard
                currentLocation={currentLocation}
                templates={templates}
                loading={loading}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                submiting={creating || updating || deleting}
                isInvalid={isInvalid}
              />
            </TabPane>
            <TabPane tabId="/templates/reviews" className="bg-transparent">
              <CreateTemplateCard
                currentLocation={currentLocation}
                templates={templates}
                loading={loading}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                submiting={creating || updating || deleting}
                isInvalid={isInvalid}
              />
            </TabPane>
            <TabPane tabId="/templates/urls" className="bg-transparent">
              <CreateTemplateCard
                currentLocation={currentLocation}
                templates={templates}
                loading={loading}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                submiting={creating || updating || deleting}
                isInvalid={isInvalid}
              />
            </TabPane>
          </TabContent>
        </div>
      </Col>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => setAlertVisible(false)}
        confirmBtnText="Close"
        body={modalMessage}
        title="Success"
      />
    </>
  )
}

Button.Edit = styled(Button)`
  background: #eaeaea !important;
  background-color: #eaeaea !important;
  border-radius: 20px;
  color: #929292 !important;

  svg {
    color: #929292;
    margin-right: 8px;
  }

  &:focus {
    color: #929292 !important;
  }

  &:hover {
    color: #fff !important;
    svg {
      color: #fff !important;
    }
  }
`

export default TextTemplates
